import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { QrCode, Copy, Download, Share, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { companyService } from '../../services/companyService';

const CustomerRegistrationQR = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [company, setCompany] = useState(null);

  // URL para auto-cadastro do cliente
  const registrationUrl = `${window.location.origin}/cadastro-cliente/${user?.companyId}`;

  // Carregar dados da empresa
  useEffect(() => {
    const loadCompany = async () => {
      if (user?.companyId) {
        try {
          const companyData = await companyService.getById(user.companyId);
          setCompany(companyData);
        } catch (error) {
          console.error('Erro ao carregar dados da empresa:', error);
        }
      }
    };

    loadCompany();
  }, [user?.companyId]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(registrationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar URL:', err);
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = registrationUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('customer-registration-qr');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-code-cadastro-${user?.companyId}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Cadastre-se como cliente',
          text: 'Use este link para se cadastrar como cliente:',
          url: registrationUrl,
        });
      } catch (err) {
        console.error('Erro ao compartilhar:', err);
        handleCopyUrl();
      }
    } else {
      handleCopyUrl();
    }
  };

  const openRegistrationPage = () => {
    window.open(registrationUrl, '_blank');
  };

  if (!user?.companyId) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-600">Empresa não identificada</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center mb-4">
        <QrCode className="w-6 h-6 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-800">QR Code para Auto-Cadastro</h3>
      </div>
      
      <p className="text-gray-600 mb-6">
        Compartilhe este QR Code com seus clientes para que eles possam se cadastrar automaticamente no seu programa de fidelidade.
      </p>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* QR Code */}
        <div className="flex-shrink-0">
          <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block">
            <QRCode
              id="customer-registration-qr"
              value={registrationUrl}
              size={200}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              viewBox={`0 0 200 200`}
            />
          </div>
        </div>

        {/* Informações e ações */}
        <div className="flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link de Cadastro:
            </label>
            <div className="flex">
              <input
                type="text"
                value={registrationUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg bg-gray-50 text-sm"
              />
              <button
                onClick={handleCopyUrl}
                className={`px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg transition-colors ${
                  copied 
                    ? 'bg-green-50 text-green-700 border-green-300' 
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                title="Copiar link"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {copied && (
              <p className="text-sm text-green-600 mt-1">Link copiado!</p>
            )}
          </div>

          {/* Botões de ação */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={handleDownloadQR}
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar QR Code
            </button>
            
            <button
              onClick={handleShare}
              className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Share className="w-4 h-4 mr-2" />
              Compartilhar
            </button>
            
            <button
              onClick={openRegistrationPage}
              className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors sm:col-span-2"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Testar Página de Cadastro
            </button>
          </div>

          {/* Instruções */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-medium text-blue-900 mb-2">Como usar:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Imprima o QR Code e coloque em local visível</li>
              <li>• Clientes escaneiam com a câmera do celular</li>
              <li>• Eles serão direcionados para o formulário de cadastro</li>
              {company?.welcomeBonus > 0 ? (
                <li>• Após o cadastro, recebem {company.welcomeBonus} pontos de bônus</li>
              ) : (
                <li>• Após o cadastro, já poderão acumular pontos</li>
              )}
              <li>• Ganham {company?.pointsPerReal || 1} ponto para cada R$ 1,00 gasto</li>
            </ul>
            
            {company?.welcomeBonus === 0 && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs text-yellow-700">
                  💡 Dica: Configure um bônus de boas-vindas nas configurações para incentivar mais cadastros!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegistrationQR;
